/** Landing: the story in three beats (plan, ask everyone, see who understood) told on the
 * board, then the two doors. The only authored motion in the product lives here: the demo
 * check filling in as the teacher would see it. */
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSession } from "../auth/SessionContext";
import { useSignupPolicy } from "../auth/useSignupPolicy";
import { LANGS, setLang, type Lang } from "../i18n";
import { Reach } from "../landing/Reach";
import { capabilities } from "../platform/capabilities";
import { useSurface } from "../platform/surface";
import { product } from "../product";
import { Button } from "../ui";
import { Join } from "./Join";

const DEMO = [
  { key: "A", pct: 12, correct: false },
  { key: "B", pct: 61, correct: true },
  { key: "C", pct: 23, correct: false },
  { key: "D", pct: 4, correct: false },
];

const primary = "inline-flex min-h-[var(--control-h)] items-center justify-center rounded-[var(--radius-control)] bg-[color:var(--accent)] px-6 font-semibold text-[color:var(--accent-ink)] hover:brightness-95 active:brightness-90";

/** The Student app opens on the join screen; the browser and the teacher shell get the story. */
export function Home() {
  if (capabilities.shell === "student") return <Join home />;
  return <Landing />;
}

function Landing() {
  useSurface("board");
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { principal } = useSession();
  const { policy } = useSignupPolicy();
  const open = !principal && policy === "open";
  const teacherTo = principal ? "/t" : open ? "/t/signup" : "/signin";
  const [code, setCode] = useState("");
  const lang = i18n.language.startsWith("ru") ? "ru" : "en";
  const tagline = product.tagline[lang] ?? product.tagline.en ?? "";

  return (
    <main className="min-h-dvh overflow-x-clip bg-[color:var(--bg)]">
      <section className="board px-6 pb-16 pt-6 md:px-10">
        <header className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <span className="wordmark min-w-0 truncate text-2xl" translate="no">{product.name}</span>
          <label className="shrink-0 text-sm">
            <span className="sr-only">{t("app.language")}</span>
            <select className="field field-sm" value={i18n.language} onChange={(e) => setLang(e.target.value as Lang)}>
              {LANGS.map((l) => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </select>
          </label>
        </header>

        <div className="mx-auto mt-14 grid max-w-5xl items-center gap-12 md:grid-cols-[1.1fr_1fr]">
          <div className="rise min-w-0">
            <h1 className="display wrap-anywhere text-[1.75rem] leading-[1.12] sm:text-4xl md:text-5xl">{tagline}</h1>
            <p className="mt-6 max-w-prose text-lg text-[color:var(--ink-2)]">{t("home.lede")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={teacherTo} className={primary}>
                {principal ? t("teacher.courses") : t("home.teacherCta")}
              </Link>
              <a href="#join" className="inline-flex min-h-[var(--control-h)] items-center justify-center rounded-[var(--radius-control)] border border-[color:var(--line)] px-6 font-semibold hover:bg-[color:var(--surface)]">
                {t("home.studentCta")}
              </a>
            </div>
          </div>

          <figure className="rise rounded-[var(--radius-sheet)] bg-[color:var(--surface)] p-5 shadow-md" style={{ animationDelay: "150ms" }}>
            <figcaption className="flex items-center justify-between text-sm text-[color:var(--ink-2)]">
              <span>{t("home.demoLabel")}</span>
              <span className="bignum text-base">0:40</span>
            </figcaption>
            <p className="mt-3 text-lg font-semibold">{t("home.demoStem")}</p>
            <ul className="mt-4 flex flex-col gap-2" aria-hidden>
              {DEMO.map((o, i) => (
                <li key={o.key} className="grid grid-cols-[2rem_1fr_3.5rem] items-center gap-3">
                  <span className="bignum text-lg">{o.key}</span>
                  <div className="h-6 min-w-0 overflow-hidden rounded-sm bg-[color:var(--surface-2)]">
                    <div className="demo-bar h-full rounded-sm" style={{ width: `${o.pct}%`, background: o.correct ? "var(--marker)" : "var(--chalk-2)", animationDelay: `${400 + i * 120}ms` }} />
                  </div>
                  <span className="num text-right text-sm">{o.pct}%</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-[color:var(--ink-2)]">{t("home.demoFooter")}</p>
          </figure>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-16 md:px-10">
        <ol className="grid gap-10 md:grid-cols-3">
          {(["plan", "ask", "see"] as const).map((beat) => (
            <li key={beat} className="flex flex-col gap-3">
              <h2 className="display text-2xl">{t(`home.beats.${beat}.title`)}</h2>
              <p className="text-[color:var(--ink-2)]">{t(`home.beats.${beat}.body`)}</p>
              <p className="text-sm font-medium">{t(`home.beats.${beat}.note`)}</p>
            </li>
          ))}
        </ol>
      </section>

      <Reach />

      {/* minmax(0,1fr) tracks: an input's intrinsic width must never set the card's width */}
      <section id="join" className="mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)] gap-6 px-6 pb-20 md:grid-cols-2 md:px-10">
        <div className="min-w-0 rounded-[var(--radius-sheet)] bg-[color:var(--surface)] p-6 shadow-sm">
          <h2 className="display text-xl">{t("home.students")}</h2>
          <p className="mb-4 mt-1 text-sm text-[color:var(--ink-2)]">{t("home.studentsHint")}</p>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const c = code.trim().toUpperCase();
              if (c) void navigate({ to: "/j/$code", params: { code: c } });
            }}
          >
            <input
              className="field bignum min-h-14 min-w-0 flex-1 text-xl uppercase"
              placeholder={t("join.codePlaceholder")}
              aria-label={t("join.code")}
              name="code"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={8}
              size={8}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button type="submit">{t("join.cta")}</Button>
          </form>
        </div>
        <div className="min-w-0 rounded-[var(--radius-sheet)] bg-[color:var(--surface)] p-6 shadow-sm">
          <h2 className="display text-xl">{t("home.teachers")}</h2>
          <p className="mb-4 mt-1 text-sm text-[color:var(--ink-2)]">{t(principal || open ? "home.teachersHint" : "home.teachersHintInvite")}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to={teacherTo} className={primary}>
              {principal ? t("teacher.courses") : open ? t("teacherSignup.cta") : t("signin.cta")}
            </Link>
            {open && (
              <Link to="/signin" className="inline-flex min-h-[var(--control-h)] items-center font-medium underline underline-offset-4 hover:decoration-2">
                {t("signin.cta")}
              </Link>
            )}
          </div>
        </div>
        <p className="text-sm text-[color:var(--ink-2)] md:col-span-2">{t("home.footer")}</p>
      </section>
    </main>
  );
}
